import apis from "../../../utils/apis";
import { showFeedbackModal } from "../UserFeedbackActions";
import { AppThunk } from "../../types";

interface InsightType {
  id: string;
  name: string;
  description: string;
  category: string;
  tags?: string[];
  created_by: string;
  created_by_logo: string;
  last_updated: string;
  message_path: string;
  type: string;
}

interface InsightMetadata {
  insight_type_id: string;
  name: string;
  status: 'processing' | 'completed' | 'failed' | 'queued';
  generated_at: string;
  word_count: number;
  version: number;
  error_message?: string | null;
}

interface InsightListData {
  insights: InsightMetadata[];
  last_updated: string;
}

export const fetchInsightTypes = (): AppThunk<Promise<InsightType[]>> =>
  async (dispatch) => {
    try {
      const response = await apis.get('/insights/document-types');
      if (response.status === 200) {
        return response.data.strategies || [];
      }
      return [];
    } catch (error) {
      console.error('Failed to load insight types:', error);
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: 'Failed to load insight types',
          modalType: 'error',
          duration: 5000
        })
      );
      return [];
    }
  };


export const fetchInsightsList = (matterUuid: string): AppThunk<Promise<InsightMetadata[]>> =>
  async (dispatch) => {
    try {
      const response = await apis.get(`/insights/list/${matterUuid}`);
      if (response.status === 200) {
        return response.data.insights || [];
      }
      return [];
    } catch (error) {
      console.error('Failed to load insights list:', error);
      return [];
    }
  };

  export const setMatterCustomInstruction = (matterId: string, insightId: number, instruction: string) => async (dispatch: any) => {
    try {
      const response = await apis.post(`/matter-insight/${matterId}/${insightId}/custom_instruction`, { instruction });
      dispatch(showFeedbackModal({ showModal: true, message: 'Custom instruction updated successfully!', modalType: 'success', duration: 3000 }));
      return response.data;
    } catch (error) {
      console.error('Error setting matter custom instruction:', error);
      throw error;
    }
  };

export const finalizeInsight = (
  matterUuid: string,
  insightTypeId: string
): AppThunk<Promise<boolean>> => {
  return async (dispatch) => {
    try {
      const response = await apis.post(
        `/insights/${matterUuid}/${insightTypeId}/finalize`
      );

      if (response.status === 200) {
        const completionTime = new Date().toLocaleString('en-GB', {
          dateStyle: 'short',
          timeStyle: 'medium',
          hour12: false
        });

        dispatch(
          showFeedbackModal({
            showModal: true,
            message: `Insight finalized at ${completionTime}`,
            modalType: 'success',
            duration: 4000
          })
        );

        // Log completion
        console.log(`[INSIGHT_FINALIZED] ${insightTypeId} finalized at ${completionTime}`, {
          matterUuid,
          insightTypeId,
          completionTime,
          filesFolder: response.data.files_folder_id,
          filesFile: response.data.files_file_id
        });

        return true;
      } else {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: 'Failed to finalize insight',
            modalType: 'error',
            duration: 3000
          })
        );
        return false;
      }
    } catch (err) {
      console.error('Error finalizing insight:', err);
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: 'Failed to finalize insight',
          modalType: 'error',
          duration: 3000
        })
      );
      return false;
    }
  };
};

export const unfinalizeInsight = (
  matterUuid: string,
  insightTypeId: string
): AppThunk<Promise<boolean>> => {
  return async (dispatch) => {
    try {
      const response = await apis.post(
        `/insights/${matterUuid}/${insightTypeId}/unfinalize`
      );

      if (response.status === 200) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: 'Insight restoration initiated',
            modalType: 'success',
            duration: 3000
          })
        );

        console.log(`[INSIGHT_UNFINALIZED] ${insightTypeId} restoration initiated`, {
          matterUuid,
          insightTypeId
        });

        return true;
      } else {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: 'Failed to restore insight',
            modalType: 'error',
            duration: 3000
          })
        );
        return false;
      }
    } catch (err) {
      console.error('Error unfinalizing insight:', err);
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: 'Failed to restore insight',
          modalType: 'error',
          duration: 3000
        })
      );
      return false;
    }
  };
};
